import React from 'react';
import { useTranslation } from 'react-i18next';

// ── "Awaiting approval" marker (owner 2026-08-28) ───────────────────────────────────────────────
// Shown right NEXT TO THE DOCUMENT NUMBER wherever a quotation / invoice / receipt is held up:
//   • quotation — the document gate itself (no PDF, no email until someone approves)
//   • invoice / receipt — an email for it is frozen in the outgoing-approval queue
// One amber pill for both, so the operator learns a single visual.
// Compact Bangkok stamp — the year is noise next to the pill it sits beside.
const shortStamp = (ts: number) => ts
  ? new Date(ts * 1000).toLocaleString('en-GB', { timeZone: 'Asia/Bangkok', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
  : '';

// "calem@samuilawfirm.com, nick@slf-accounting.co" → "Calem+Nick". The operator needs to see WHO
// was asked at a glance (owner 2026-08-30: "我们无法得知邮件是否发出去了，发给谁了") — a tooltip is
// not an answer, nobody hovers a whole list.
export function approverNames(to?: string): string {
  const parts = String(to || '').split(/[,;]/).map(x => x.trim()).filter(Boolean);
  if (!parts.length) return '';
  return parts
    .map(e => (e.split('@')[0] || e).replace(/[._-]+/g, ' ').trim())
    .map(n => n.charAt(0).toUpperCase() + n.slice(1))
    .join('+');
}

/** The green "request was emailed" / red "it was not" chip — shared so every list shows one thing. */
export function ApprovalMailChip({ notifiedAt, notifiedTo, style }: { notifiedAt?: number; notifiedTo?: string; style?: React.CSSProperties }) {
  const { t } = useTranslation();
  const pill: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 800,
    letterSpacing: 0.2, borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap', ...style,
  };
  if (!notifiedAt) {
    return (
      <span title={t('tr_appr_not_mailed_hint')} style={{ ...pill, color: '#B42318', background: '#FCEBEB', border: '1px solid #F5B5B5' }}>
        <i className="ti ti-mail-off" style={{ fontSize: 11 }} aria-hidden />{t('tr_appr_not_mailed')}
      </span>
    );
  }
  const who = approverNames(notifiedTo);
  return (
    <span title={`${t('tr_appr_mailed_to')}: ${notifiedTo || '—'}`}
      style={{ ...pill, color: '#276749', background: '#E3EFE8', border: '1px solid #9BD8B3' }}>
      <i className="ti ti-mail-check" style={{ fontSize: 11 }} aria-hidden />
      {who ? `${who} · ` : ''}{shortStamp(notifiedAt)}
    </span>
  );
}

/**
 * `notifiedAt` / `notifiedTo` (owner 2026-08-30): the ✉N badge beside a document counts emails to
 * the CLIENT, so a document held for approval showed nothing about the approval REQUEST — the
 * operator could not tell whether Calem/Nick had actually been told ("我也不知道邮件是发了还是没发").
 * Passing the stamp adds a second, colour-coded chip: green with the time when the request really
 * went out, red when it did not (a document created before automatic requests, or a failed send —
 * both are fixed by the Approval-link button). Omit the props and the badge renders as before.
 */
export default function ApprovalBadge({ title, notifiedAt, notifiedTo }: { title?: string; notifiedAt?: number; notifiedTo?: string }) {
  const { t } = useTranslation();
  const pill: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 3,
    fontSize: 10, fontWeight: 800, letterSpacing: 0.2, borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap',
  };
  // Own LINE under the document number, not trailing after it (owner 2026-08-30): two pills inline
  // pushed the Doc No. column wide enough to squeeze the rest of the table. `flex` makes the group
  // block-level so it drops below the number, and wraps again if a third pill is ever added.
  return (
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, marginTop: 3 }}>
      <span title={title || t('appr_badge_hint')}
        style={{ ...pill, color: '#92400E', background: '#FEF3C7', border: '1px solid #FBD38D' }}>
        <i className="ti ti-clock-pause" style={{ fontSize: 11 }} aria-hidden />{t('appr_badge')}
      </span>
      {notifiedAt !== undefined && <ApprovalMailChip notifiedAt={notifiedAt} notifiedTo={notifiedTo} />}
    </span>
  );
}
